import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  Message,
  Modal,
  Pagination,
  Popover,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import {
  ArrowDown,
  ArrowUp,
  Columns3,
  ContactRound,
  Pencil,
  Plus,
  Power,
  RotateCcw,
  Search,
  Trash2,
  UserRoundCheck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCurrentAuthUser } from "../../auth";
import { canAccessCrmOperation, canUsePermission, type PermissionUser } from "../../../lib/auth";
import { CrmListFilterMoreButton } from "../../../shared/ui/CrmListFilterMoreButton";
import { SearchHistoryInput } from "../../../shared/ui/SearchHistoryInput";
import { stabilizeLayoutMetric } from "../../../shared/ui/stabilizeLayoutMetric";
import {
  WorkbenchColumnSettings,
  loadWorkbenchVisibleColumnsPreference,
  normalizeWorkbenchVisibleColumns,
  saveWorkbenchVisibleColumnsPreference,
} from "../../../shared/ui/WorkbenchColumnSettings";
import {
  CRM_CUSTOMER_WORKBENCH_VISIBLE_COLUMNS_STORAGE_KEY,
  crmCustomerWorkbenchColumnOptions,
  crmCustomerWorkbenchDefaultVisibleColumns,
} from "../config";
import {
  CUSTOMER_STAR_OPTION_DESCRIPTIONS,
  CUSTOMER_STAR_OPTIONS,
  shouldShowCustomerStar,
  validateCustomerStarForSave,
} from "../config/customerStar";
import {
  rememberSearchHistory,
  SEARCH_HISTORY_KEYS,
} from "../../../lib/searchHistoryStorage";

import { useCrmCustomerWorkbenchRuntime } from "../runtime/useCrmCustomerWorkbenchRuntime";
import {
  formatCustomerArea,
  formatCustomerOwners,
  formatCustomerStaffOptionLabel,
} from "../service";
import type {
  CustomerAssignmentRelationType,
  CustomerWorkbenchDetail,
  CustomerWorkbenchFormPayload,
  CustomerWorkbenchItem,
  CustomerWorkbenchNlQueryParseResult,
  CustomerWorkbenchOrganizationOption,
  CustomerWorkbenchOrganizationUnitOption,
  CustomerWorkbenchSelectionMode,
  CustomerWorkbenchColumnKey,
  CustomerWorkbenchStaffOption,
  CustomerWorkbenchViewScope,
} from "../types";
import "./CrmCustomerWorkbenchPage.css";
import "../../../shared/ui/crmListPageLayout.css";
import "../../../shared/ui/adminEditorForm.css";

type CustomerFormState = {
  mode: "create" | "edit";
  customerId?: number;
  draft: CustomerWorkbenchFormPayload;
  initialOrganization?: CustomerWorkbenchOrganizationOption;
  initialUnit?: CustomerWorkbenchOrganizationUnitOption;
};

type SharedOwnerEditContext = {
  customerId: number;
  customerName: string;
  currentUserIds: number[];
};

type SharedOwnerDeleteContext = {
  item: CustomerWorkbenchItem;
  userId: number;
  userName: string;
};

const defaultCustomerDraft: CustomerWorkbenchFormPayload = {
  customerName: "",
  orgId: 0,
  anchorOrgUnitId: undefined,
  anchorLevel: "school",
  coverageMode: "anchor",
  businessStatus: "潜在",
  customerStar: undefined,
  remark: "",
  currentUserId: 1,
};

const customerBusinessStatusOptions = [
  "A1",
  "A2",
  "B1",
  "B2",
  "B3",
  "C1",
  "C2",
  "C3",
  "潜在",
  "无效",
  "问题客户",
];

/** CRM 客户工作台主页面。 */
export function CrmCustomerWorkbenchFeaturePage() {
  const runtime = useCrmCustomerWorkbenchRuntime();
  const navigate = useNavigate();
  const currentUserQuery = useCurrentAuthUser();
  const permissionUser = currentUserQuery.data;
  const tableShellRef = useRef<HTMLDivElement | null>(null);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const syncingScrollRef = useRef(false);
  const [topScrollWidth, setTopScrollWidth] = useState(0);
  const [tableScrollY, setTableScrollY] = useState(520);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [selectionMode, setSelectionMode] =
    useState<CustomerWorkbenchSelectionMode>("selected_rows");
  const [assignmentVisible, setAssignmentVisible] = useState(false);
  const [assignmentType, setAssignmentType] =
    useState<CustomerAssignmentRelationType>("primary_owner");
  const [targetUserId, setTargetUserId] = useState<number>();
  const [sharedOwnerModalVisible, setSharedOwnerModalVisible] = useState(false);
  const [targetSharedOwnerUserIds, setTargetSharedOwnerUserIds] = useState<number[]>([]);
  const [sharedOwnerEditContext, setSharedOwnerEditContext] =
    useState<SharedOwnerEditContext | null>(null);
  const [sharedOwnerDeleteConfirm, setSharedOwnerDeleteConfirm] =
    useState<SharedOwnerDeleteContext | null>(null);
  const [customerForm, setCustomerForm] = useState<CustomerFormState | null>(null);
  const [advancedFiltersVisible, setAdvancedFiltersVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<CustomerWorkbenchColumnKey[]>(() =>
    loadWorkbenchVisibleColumnsPreference(
      CRM_CUSTOMER_WORKBENCH_VISIBLE_COLUMNS_STORAGE_KEY,
      crmCustomerWorkbenchColumnOptions,
      crmCustomerWorkbenchDefaultVisibleColumns,
    ),
  );

  const handleVisibleColumnsChange = useCallback((columns: CustomerWorkbenchColumnKey[]) => {
    const normalizedColumns = normalizeWorkbenchVisibleColumns(
      columns,
      crmCustomerWorkbenchColumnOptions,
      crmCustomerWorkbenchDefaultVisibleColumns,
    );
    setVisibleColumns(normalizedColumns);
    saveWorkbenchVisibleColumnsPreference(
      CRM_CUSTOMER_WORKBENCH_VISIBLE_COLUMNS_STORAGE_KEY,
      normalizedColumns,
    );
  }, []);

  const handleOpenOpportunities = useCallback(
    (item: CustomerWorkbenchItem) => {
      const params = new URLSearchParams();
      params.set("customerKeyword", item.customerName);
      navigate(`/crm/opportunities/workbench?${params.toString()}`);
    },
    [navigate],
  );

  const columns = useMemo(
    () =>
      buildCustomerColumns({
        visibleColumns,
        permissionUser,
        sortField: runtime.sortField,
        sortDirection: runtime.sortDirection,
        onSort: runtime.handleSort,
        onOpenContacts: runtime.handleOpenContacts,
        onOpenOpportunities: handleOpenOpportunities,
        onOpenCustomer: openEditForm,
        onAssignPrimaryOwner: (item) => openAssignment("primary_owner", [item.id]),
        onOpenSharedOwnerEditor: handleOpenSharedOwnerEditor,
        onRemoveSharedOwner: handleRemoveSharedOwner,
        onDisableCustomer: (item) => void runtime.handleDisableCustomer(item.id),
        onEnableCustomer: (item) => void runtime.handleEnableCustomer(item.id),
        onDeleteCustomer: (item) => void runtime.handleDeleteCustomer(item.id),
      }),
    [
      handleOpenOpportunities,
      permissionUser,
      runtime.handleOpenContacts,
      runtime.handleSort,
      runtime.sortDirection,
      runtime.sortField,
      visibleColumns,
    ],
  );

  const tableScrollWidth = columns.reduce(
    (total, column) => total + (typeof column.width === "number" ? column.width : 0),
    40,
  );

  const findTableScroller = useCallback(() => {
    const tableShell = tableShellRef.current;
    return (
      tableShell?.querySelector<HTMLElement>(".arco-table-body") ??
      tableShell?.querySelector<HTMLElement>(".arco-table-content-inner") ??
      tableShell?.querySelector<HTMLElement>(".arco-table-content-scroll") ??
      null
    );
  }, []);

  const syncHorizontalScrollFrom = useCallback(
    (source: HTMLElement) => {
      if (syncingScrollRef.current) {
        return;
      }
      const peers = [
        topScrollRef.current,
        bottomScrollRef.current,
        findTableScroller(),
      ].filter((node): node is HTMLElement => Boolean(node) && node !== source);
      if (peers.length === 0) {
        return;
      }
      syncingScrollRef.current = true;
      const nextLeft = source.scrollLeft;
      for (const peer of peers) {
        if (peer.scrollLeft !== nextLeft) {
          peer.scrollLeft = nextLeft;
        }
      }
      window.requestAnimationFrame(() => {
        syncingScrollRef.current = false;
      });
    },
    [findTableScroller],
  );

  const handleTopScroll = useCallback(() => {
    const topScroll = topScrollRef.current;
    if (!topScroll) {
      return;
    }
    syncHorizontalScrollFrom(topScroll);
  }, [syncHorizontalScrollFrom]);

  const handleBottomScroll = useCallback(() => {
    const bottomScroll = bottomScrollRef.current;
    if (!bottomScroll) {
      return;
    }
    syncHorizontalScrollFrom(bottomScroll);
  }, [syncHorizontalScrollFrom]);

  useEffect(() => {
    const topScroll = topScrollRef.current;
    const bottomScroll = bottomScrollRef.current;
    const tableScroller = findTableScroller();
    if (!topScroll || !bottomScroll || !tableScroller) {
      return undefined;
    }

    const updateMetrics = () => {
      setTopScrollWidth(tableScroller.scrollWidth);
      const nextLeft = tableScroller.scrollLeft;
      if (topScroll.scrollLeft !== nextLeft) {
        topScroll.scrollLeft = nextLeft;
      }
      if (bottomScroll.scrollLeft !== nextLeft) {
        bottomScroll.scrollLeft = nextLeft;
      }
    };
    const handleTableScroll = () => syncHorizontalScrollFrom(tableScroller);
    tableScroller.addEventListener("scroll", handleTableScroll);
    window.addEventListener("resize", updateMetrics);
    updateMetrics();

    const observer =
      typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updateMetrics);
    observer?.observe(tableScroller);
    if (tableScroller.firstElementChild instanceof HTMLElement) {
      observer?.observe(tableScroller.firstElementChild);
    }

    return () => {
      tableScroller.removeEventListener("scroll", handleTableScroll);
      window.removeEventListener("resize", updateMetrics);
      observer?.disconnect();
    };
  }, [findTableScroller, runtime.items.length, syncHorizontalScrollFrom, tableScrollWidth]);

  useEffect(() => {
    const tableShell = tableShellRef.current;
    if (!tableShell) {
      return undefined;
    }

    const updateTableScrollY = () => {
      const table = tableShell.querySelector(".arco-table");
      if (!(table instanceof HTMLElement)) {
        return;
      }
      const top = table.getBoundingClientRect().top;
      const pagination = table.querySelector(".arco-pagination");
      const paginationHeight =
        pagination instanceof HTMLElement ? pagination.getBoundingClientRect().height : 52;
      const bottomScroll = bottomScrollRef.current;
      const bottomScrollHeight =
        bottomScroll instanceof HTMLElement ? bottomScroll.getBoundingClientRect().height + 6 : 18;
      const nextScrollY = Math.max(
        320,
        Math.floor(window.innerHeight - top - paginationHeight - bottomScrollHeight - 12),
      );
      setTableScrollY((previous) => stabilizeLayoutMetric(previous, nextScrollY));
    };

    updateTableScrollY();
    window.addEventListener("resize", updateTableScrollY);
    return () => window.removeEventListener("resize", updateTableScrollY);
  }, [advancedFiltersVisible, runtime.items.length, tableScrollWidth]);

  const currentPageCustomerIds = useMemo(
    () =>
      runtime.items
        .filter((item) =>
          canAccessCrmOperation(permissionUser, "crm.customer:assign", item.canAssign),
        )
        .map((item) => item.id),
    [permissionUser, runtime.items],
  );
  const displayedSelectedRowKeys =
    selectionMode === "all_results" ? currentPageCustomerIds : selectedRowKeys;
  const selectedCount =
    selectionMode === "all_results" ? runtime.total : selectedRowKeys.length;
  const hasSelectedCustomers = selectedCount > 0;
  const currentPageScopeActive = isCurrentPageSelectionActive(
    selectionMode,
    selectedRowKeys,
    currentPageCustomerIds,
  );
  const allResultsScopeActive = selectionMode === "all_results";
  const canAssignSelectedCustomers =
    hasSelectedCustomers && canUsePermission(permissionUser, "crm.customer:assign");

  useEffect(() => {
    setSelectionMode("selected_rows");
    setSelectedRowKeys([]);
  }, [runtime.submittedFilters, runtime.sortField, runtime.sortDirection, runtime.viewScope]);

  /** 选择当前页可分配客户。 */
  function handleSelectCurrentPage() {
    setSelectionMode("selected_rows");
    setSelectedRowKeys(currentPageCustomerIds);
  }

  /** 选择当前查询条件下全部客户。 */
  function handleSelectAllResults() {
    if (runtime.total === 0) {
      return;
    }
    setSelectionMode("all_results");
    setSelectedRowKeys(currentPageCustomerIds);
  }

  /** 清空批量选择。 */
  function handleClearSelection() {
    setSelectionMode("selected_rows");
    setSelectedRowKeys([]);
  }

  /** 将当前选择状态解析为可提交的客户 ID 列表。 */
  async function resolveSelectedCustomerIds() {
    return runtime.handleResolveSelectedCustomerIds({
      mode: selectionMode,
      customerIds: selectedRowKeys,
    });
  }

  /** 打开客户编辑表单。 */
  function openEditForm(item: CustomerWorkbenchItem) {
    runtime.handleOpenCustomer(item.id);
    setCustomerForm({
      mode: "edit",
      customerId: item.id,
      initialOrganization: {
        id: item.orgId,
        orgName: item.orgName,
        province: item.province,
        city: item.city,
      },
      initialUnit: item.orgUnitId
        ? {
            id: item.orgUnitId,
            orgId: item.orgId,
            unitName: item.orgUnitName ?? item.orgUnitPathName ?? "",
            unitPathName: item.orgUnitPathName ?? item.orgUnitName ?? "",
            levelNo: mapAnchorLevelToLevelNo(item.anchorLevel),
          }
        : undefined,
      draft: {
        customerName: item.customerName,
        orgId: item.orgId,
        anchorOrgUnitId: item.orgUnitId,
        anchorLevel: item.anchorLevel,
        coverageMode: "anchor",
        businessStatus: item.businessStatus,
        customerStar: undefined,
        anchorUnitType: undefined,
        remark: "",
        currentUserId: 1,
      },
    });
  }

  useEffect(() => {
    if (!customerForm || customerForm.mode !== "edit") {
      return;
    }
    const detail = runtime.selectedCustomerDetail;
    if (!detail || detail.id !== customerForm.customerId) {
      return;
    }
    const nextStar = detail.customerStar;
    const nextUnitType = detail.anchorOrgUnitType;
    const nextRemark = detail.remark ?? "";
    const nextCoverage = detail.coverageMode;
    if (
      customerForm.draft.customerStar === nextStar &&
      customerForm.draft.anchorUnitType === nextUnitType &&
      customerForm.draft.remark === nextRemark &&
      customerForm.draft.coverageMode === nextCoverage
    ) {
      return;
    }
    setCustomerForm({
      ...customerForm,
      initialUnit: customerForm.initialUnit
        ? {
            ...customerForm.initialUnit,
            unitType: nextUnitType ?? customerForm.initialUnit.unitType,
          }
        : customerForm.initialUnit,
      draft: {
        ...customerForm.draft,
        customerStar: nextStar,
        anchorUnitType: nextUnitType,
        remark: nextRemark,
        coverageMode: nextCoverage || customerForm.draft.coverageMode,
      },
    });
  }, [customerForm, runtime.selectedCustomerDetail]);

  /** 打开客户新增表单。 */
  function openCreateForm() {
    runtime.handleCloseCustomer();
    setCustomerForm({
      mode: "create",
      draft: { ...defaultCustomerDraft },
    });
  }

  /** 打开销售分配弹窗。 */
  async function openAssignment(
    type: CustomerAssignmentRelationType,
    customerIds?: number[],
  ) {
    const resolvedIds =
      customerIds ??
      (await resolveSelectedCustomerIds());
    if (resolvedIds.length === 0) {
      return;
    }
    setAssignmentType(type);
    setSelectionMode("selected_rows");
    setSelectedRowKeys(resolvedIds);
    setTargetUserId(undefined);
    setAssignmentVisible(true);
  }

  /** 确认销售分配。 */
  async function handleConfirmAssignment() {
    if (!targetUserId || selectedRowKeys.length === 0) {
      return;
    }
    await runtime.handleConfirmAssignment({
      customerIds: selectedRowKeys,
      targetUserId,
      relationType: assignmentType,
      conflictStrategy: assignmentType === "primary_owner" ? "overwrite_primary" : "add_shared",
      currentUserId: 1,
      permissionLevel: assignmentType === "primary_owner" ? "manage" : "follow",
      sourceType: "manual",
      reason:
        assignmentType === "primary_owner"
          ? "客户工作台分配主销售"
          : "客户工作台添加共享销售",
    });
    setAssignmentVisible(false);
    handleClearSelection();
  }

  /** 打开单个客户的共享销售编辑弹窗。 */
  function handleOpenSharedOwnerEditor(item: CustomerWorkbenchItem) {
    const currentUserIds = item.sharedOwnerUserIds ?? [];
    setSharedOwnerEditContext({
      customerId: item.id,
      customerName: item.customerName,
      currentUserIds,
    });
    setTargetSharedOwnerUserIds(currentUserIds);
    setSharedOwnerModalVisible(true);
  }

  /** 按目标集合保存单个客户的共享销售。 */
  function handleConfirmSharedOwners() {
    if (!sharedOwnerEditContext) {
      return;
    }
    const context = sharedOwnerEditContext;
    const targetUserIds = [...targetSharedOwnerUserIds];
    setSharedOwnerModalVisible(false);
    setSharedOwnerEditContext(null);
    setTargetSharedOwnerUserIds([]);
    void runtime.handleReplaceCustomerSharedOwners(
      context.customerId,
      context.currentUserIds,
      targetUserIds,
    );
  }

  /** 请求移除单个客户的一个共享销售。 */
  function handleRemoveSharedOwner(
    item: CustomerWorkbenchItem,
    userId: number,
    userName: string,
  ) {
    setSharedOwnerDeleteConfirm({ item, userId, userName });
  }

  /** 确认移除单个客户的一个共享销售。 */
  function handleConfirmRemoveSharedOwner() {
    if (!sharedOwnerDeleteConfirm) {
      return;
    }
    const { item, userId } = sharedOwnerDeleteConfirm;
    const currentUserIds = item.sharedOwnerUserIds ?? [];
    const targetUserIds = currentUserIds.filter((currentUserId) => currentUserId !== userId);
    setSharedOwnerDeleteConfirm(null);
    void runtime.handleReplaceCustomerSharedOwners(item.id, currentUserIds, targetUserIds);
  }

  /** 保存客户表单。 */
  async function handleSaveCustomer() {
    if (!customerForm || !customerForm.draft.customerName.trim() || !customerForm.draft.orgId) {
      return;
    }
    const unitType = customerForm.draft.anchorUnitType;
    const starError = validateCustomerStarForSave(customerForm.draft, unitType);
    if (starError) {
      Message.error(starError);
      return;
    }
    const payload = {
      ...customerForm.draft,
      customerStar: shouldShowCustomerStar(unitType) ? customerForm.draft.customerStar : undefined,
      anchorUnitType: undefined,
    };
    await runtime.handleSaveCustomer(customerForm.customerId, payload);
    setCustomerForm(null);
  }

  /** 记住文本搜索条件并查询客户列表。 */
  function handleCustomerFilterSearch() {
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.customerRegionSchoolText,
      runtime.draftFilters.regionSchoolText ?? "",
    );
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.customerName,
      runtime.draftFilters.customerName ?? "",
    );
    runtime.handleSearch();
  }

  /** 记住智能查询并执行。 */
  function handleCustomerSmartSearch() {
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.customerNlQuery,
      runtime.draftFilters.nlQuery ?? "",
    );
    void runtime.handleSmartSearch();
  }

  return (
    <main className="crm-list-page crm-customer-workbench-page">
      <section
        className="crm-list-filter-panel crm-customer-workbench-filter-panel"
        role="search"
        aria-label="客户筛选"
        data-layout="two-tier-responsive"
      >
        <div className="crm-list-filter-fields crm-customer-workbench-filter-fields">
          <div className="crm-list-filter-primary">
            <label className="crm-list-field crm-list-field--lg crm-customer-workbench-field">
              <span>省/市/校</span>
              <SearchHistoryInput
                storageKey={SEARCH_HISTORY_KEYS.customerRegionSchoolText}
                placeholder="省、市、学校或组织单元"
                value={runtime.draftFilters.regionSchoolText ?? ""}
                onChange={(regionSchoolText) =>
                  runtime.setDraftFilters((current) => ({ ...current, regionSchoolText }))
                }
                onPressEnter={handleCustomerFilterSearch}
              />
            </label>
            <label className="crm-list-field crm-list-field--md crm-customer-workbench-field">
              <span>客户名</span>
              <SearchHistoryInput
                storageKey={SEARCH_HISTORY_KEYS.customerName}
                placeholder="客户名模糊查询"
                value={runtime.draftFilters.customerName ?? ""}
                onChange={(customerName) =>
                  runtime.setDraftFilters((current) => ({ ...current, customerName }))
                }
                onPressEnter={handleCustomerFilterSearch}
              />
            </label>
            <label className="crm-list-field crm-list-field--lg crm-customer-workbench-field">
              <span>销售</span>
              <StaffSelect
                data-testid="customer-sales-filter-select"
                allowClear
                placeholder="主销售或共享销售"
                value={runtime.draftFilters.salesUserId}
                options={mergeSalesFilterStaffOptions(
                  runtime.staffOptions,
                  runtime.draftFilters.salesUserId,
                  runtime.draftFilters.salesUserName,
                )}
                onChange={(salesUserId, salesUserName) =>
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    salesUserId,
                    salesUserName,
                  }))
                }
              />
            </label>
            <div className="crm-list-field crm-list-field--sm crm-customer-workbench-field">
              <span>状态</span>
              <Select
                data-testid="customer-disabled-filter-select"
                value={runtime.draftFilters.isDisabled ? "disabled" : "active"}
                onChange={(value) =>
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    isDisabled: value === "disabled",
                  }))
                }
              >
                <Select.Option value="active">启用</Select.Option>
                <Select.Option value="disabled">停用</Select.Option>
              </Select>
            </div>
          </div>

          {advancedFiltersVisible ? (
            <div className="crm-list-filter-advanced">
              <div className="crm-customer-workbench-nl-query">
                <label className="crm-list-field crm-list-field--xl crm-customer-workbench-field">
                  <span>智能查询</span>
                  <SearchHistoryInput
                    storageKey={SEARCH_HISTORY_KEYS.customerNlQuery}
                    placeholder="例如：北京 中央美术 油画系"
                    value={runtime.draftFilters.nlQuery ?? ""}
                    onChange={runtime.handleNlQueryChange}
                    onPressEnter={handleCustomerSmartSearch}
                  />
                </label>
                <Button
                  type="primary"
                  icon={<Search size={16} />}
                  loading={runtime.nlQueryParsing}
                  onClick={handleCustomerSmartSearch}
                >
                  智能查询
                </Button>
              </div>
              {runtime.nlQueryErrorMessage ? (
                <Alert
                  className="crm-list-alert crm-customer-workbench-alert"
                  type="error"
                  showIcon
                  content={runtime.nlQueryErrorMessage}
                />
              ) : null}
              <NlQuerySummary result={runtime.nlQueryParseResult} />
            </div>
          ) : null}

          {runtime.errorMessage ? (
            <Alert
              className="crm-list-alert crm-customer-workbench-alert"
              type="error"
              showIcon
              content={runtime.errorMessage}
            />
          ) : null}
        </div>
        <div className="crm-list-filter-actions crm-customer-workbench-actions">
          <CrmListFilterMoreButton
            expanded={advancedFiltersVisible}
            onToggle={() => setAdvancedFiltersVisible((current) => !current)}
          />
          <Button icon={<RotateCcw size={16} />} onClick={runtime.handleReset}>
            重置
          </Button>
          <Button type="primary" icon={<Search size={16} />} onClick={handleCustomerFilterSearch}>
            查询
          </Button>
        </div>
      </section>

      <section className="crm-list-panel crm-customer-workbench-table-card">
        <div className="crm-list-section-header crm-customer-workbench-table-header">
          <div className="crm-list-title-row">
            <h2>客户列表</h2>
            <Radio.Group
              type="button"
              value={runtime.viewScope}
              onChange={(value) =>
                runtime.handleViewScopeChange(value as CustomerWorkbenchViewScope)
              }
            >
              <Radio value="mine">我的客户</Radio>
              <Radio value="all">全部客户</Radio>
            </Radio.Group>
          </div>
          <div className="crm-list-table-actions">
            {canUsePermission(permissionUser, "crm.customer:access") ? (
              <Button type="primary" icon={<Plus size={16} />} onClick={openCreateForm}>
                新增客户
              </Button>
            ) : null}
            <Popover
              position="br"
              trigger="click"
              content={
                <WorkbenchColumnSettings
                  options={crmCustomerWorkbenchColumnOptions}
                  visibleColumns={visibleColumns}
                  onChange={handleVisibleColumnsChange}
                />
              }
            >
              <Button icon={<Columns3 size={14} />}>列设置</Button>
            </Popover>
          </div>
        </div>

        <div className="crm-list-panel-body">
        {hasSelectedCustomers ? (
          <div className="crm-customer-workbench-selection-bar">
            <div className="crm-customer-workbench-selection-bar__summary">
              <span className="crm-customer-workbench-selection-bar__count">
                {selectionMode === "all_results"
                  ? `已选全部 ${formatCustomerCount(runtime.total)} 个`
                  : `已选 ${formatCustomerCount(selectedCount)} 个`}
              </span>
              <span className="crm-customer-workbench-selection-bar__hint">
                可扩展选择范围或执行批量操作
              </span>
            </div>
            <div className="crm-customer-workbench-selection-bar__scopes">
              <Button
                size="small"
                type={currentPageScopeActive ? "primary" : "secondary"}
                aria-pressed={currentPageScopeActive}
                onClick={handleSelectCurrentPage}
              >
                当前页 {formatCustomerCount(currentPageCustomerIds.length)} 个
              </Button>
              <Button
                size="small"
                type={allResultsScopeActive ? "primary" : "secondary"}
                aria-pressed={allResultsScopeActive}
                onClick={handleSelectAllResults}
              >
                全部结果 {formatCustomerCount(runtime.total)} 个
              </Button>
            </div>
            <div className="crm-customer-workbench-selection-bar__actions">
              {canAssignSelectedCustomers ? (
                <>
                  <Button
                    size="small"
                    icon={<UserRoundCheck size={16} />}
                    onClick={() => void openAssignment("shared_owner")}
                  >
                    添加共享销售
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<UserRoundCheck size={16} />}
                    onClick={() => void openAssignment("primary_owner")}
                  >
                    分配主销售
                  </Button>
                </>
              ) : null}
              <Button size="small" status="danger" type="outline" onClick={handleClearSelection}>
                清除选择
              </Button>
            </div>
          </div>
        ) : null}

          <div
            className="crm-customer-workbench-table-shell"
            ref={tableShellRef}
            data-testid="customer-workbench-table-shell"
          >
            <div
              className="crm-customer-workbench-h-scroll"
              data-testid="customer-workbench-top-scroll"
              ref={topScrollRef}
              aria-label="客户列表顶部横向滚动"
              onScroll={handleTopScroll}
            >
              <div
                className="crm-customer-workbench-h-scroll-inner"
                data-testid="customer-workbench-top-scroll-inner"
                style={{ width: topScrollWidth || tableScrollWidth }}
              />
            </div>
            <Table
              rowKey="id"
              data={runtime.items}
              columns={columns}
              loading={runtime.loading}
              tableLayoutFixed
              scroll={{ x: tableScrollWidth, y: tableScrollY }}
              rowSelection={{
                selectedRowKeys: displayedSelectedRowKeys,
                checkCrossPage: true,
                preserveSelectedRowKeys: true,
                onChange: (keys) => {
                  setSelectionMode("selected_rows");
                  setSelectedRowKeys(keys.map((key) => Number(key)));
                },
                checkboxProps: (item) => ({
                  disabled: !canAccessCrmOperation(
                    permissionUser,
                    "crm.customer:assign",
                    item.canAssign,
                  ),
                }),
              }}
              pagination={{
                current: runtime.page,
                pageSize: runtime.pageSize,
                total: runtime.total,
                sizeCanChange: true,
                sizeOptions: [10, 20, 50, 100],
                showTotal: true,
                showJumper: true,
                onChange: runtime.handlePageChange,
                onPageSizeChange: runtime.handlePageSizeChange,
              }}
            />
            <div
              className="crm-customer-workbench-h-scroll"
              data-testid="customer-workbench-bottom-scroll"
              ref={bottomScrollRef}
              aria-label="客户列表底部横向滚动"
              onScroll={handleBottomScroll}
            >
              <div
                className="crm-customer-workbench-h-scroll-inner"
                data-testid="customer-workbench-bottom-scroll-inner"
                style={{ width: topScrollWidth || tableScrollWidth }}
              />
            </div>
          </div>
        </div>
      </section>

      <Drawer
        title="客户联系人"
        width={560}
        visible={Boolean(runtime.selectedCustomerId)}
        onCancel={runtime.handleCloseContacts}
        footer={null}
      >
        <ContactDrawerContent
          loading={runtime.contactsLoading}
          page={runtime.contactsPage}
          onPageChange={runtime.handleContactPageChange}
        />
      </Drawer>

      <Modal
        title={assignmentType === "primary_owner" ? "分配客户主销售" : "分配客户共享销售"}
        visible={assignmentVisible}
        confirmLoading={runtime.mutating}
        onCancel={() => setAssignmentVisible(false)}
        onOk={() => void handleConfirmAssignment()}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            {assignmentType === "primary_owner"
              ? "覆盖已有主销售会撤销旧关系，并写入客户分配日志。"
              : "仅添加共享销售，不改变客户主销售。"}
          </Typography.Text>
          <StaffSelect
            data-testid="customer-assignment-owner-select"
            placeholder="选择销售"
            value={targetUserId}
            options={runtime.staffOptions}
            onChange={setTargetUserId}
          />
        </Space>
      </Modal>

      <Modal
        title="添加共享销售/设置共享人"
        visible={sharedOwnerModalVisible}
        unmountOnExit
        confirmLoading={runtime.mutating}
        onCancel={() => {
          setSharedOwnerModalVisible(false);
          setSharedOwnerEditContext(null);
          setTargetSharedOwnerUserIds([]);
        }}
        onOk={handleConfirmSharedOwners}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text>
            {sharedOwnerEditContext
              ? `正在设置「${sharedOwnerEditContext.customerName}」的共享销售`
              : null}
          </Typography.Text>
          <Typography.Text type="secondary">
            可追加或移除共享销售，不改变客户主销售。清空后确认会移除全部共享人。
          </Typography.Text>
          <Button
            size="small"
            disabled={targetSharedOwnerUserIds.length === 0}
            onClick={() => setTargetSharedOwnerUserIds([])}
          >
            清空共享人
          </Button>
          <Select
            data-testid="customer-shared-owner-select"
            allowClear
            showSearch
            mode="multiple"
            maxTagCount={3}
            placeholder="选择共享销售"
            value={targetSharedOwnerUserIds}
            filterOption={(inputValue, option) => {
              const optionId = Number(option.props.value);
              const matchedOption = runtime.staffOptions.find((item) => item.id === optionId);
              return matchedOption
                ? buildStaffOptionSearchText(matchedOption)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                : false;
            }}
            onChange={(nextValue) => {
              const values = Array.isArray(nextValue) ? nextValue : [];
              setTargetSharedOwnerUserIds(
                values.map((item) => Number(item)).filter((item) => Number.isFinite(item)),
              );
            }}
          >
            {runtime.staffOptions.map((option) => (
              <Select.Option key={option.id} value={option.id} disabled={option.disabled}>
                {formatCustomerStaffOptionLabel(option)}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </Modal>

      <Modal
        title="移除共享销售"
        visible={Boolean(sharedOwnerDeleteConfirm)}
        confirmLoading={runtime.mutating}
        okText="确认移除"
        cancelText="取消"
        onOk={handleConfirmRemoveSharedOwner}
        onCancel={() => setSharedOwnerDeleteConfirm(null)}
      >
        {sharedOwnerDeleteConfirm
          ? `确认把「${sharedOwnerDeleteConfirm.userName}」从该客户共享销售中移除吗？`
          : null}
      </Modal>

      <Drawer
        className="crm-customer-workbench-editor-drawer"
        title={customerForm?.mode === "edit" ? "编辑客户" : "新增客户"}
        visible={Boolean(customerForm)}
        width={560}
        okText="保存"
        cancelText="取消"
        confirmLoading={runtime.mutating}
        onCancel={() => {
          setCustomerForm(null);
          runtime.handleCloseCustomer();
        }}
        onOk={() => void handleSaveCustomer()}
      >
        {customerForm ? (
          <CustomerForm
            draft={customerForm.draft}
            detail={runtime.selectedCustomerDetail}
            initialOrganization={customerForm.initialOrganization}
            initialUnit={customerForm.initialUnit}
            onSearchOrganizations={runtime.searchOrganizations}
            onSearchOrganizationUnits={runtime.searchOrganizationUnits}
            onChange={(draft) => setCustomerForm({ ...customerForm, draft })}
          />
        ) : null}
      </Drawer>
    </main>
  );
}

/** 构建客户表格列。 */
function buildCustomerColumns({
  visibleColumns,
  permissionUser,
  sortField,
  sortDirection,
  onSort,
  onOpenContacts,
  onOpenOpportunities,
  onOpenCustomer,
  onAssignPrimaryOwner,
  onOpenSharedOwnerEditor,
  onRemoveSharedOwner,
  onDisableCustomer,
  onEnableCustomer,
  onDeleteCustomer,
}: {
  visibleColumns: CustomerWorkbenchColumnKey[];
  permissionUser: PermissionUser | null | undefined;
  sortField?: string;
  sortDirection: "asc" | "desc";
  onSort: (field: string, direction: "asc" | "desc") => void;
  onOpenContacts: (customerId: number) => void;
  onOpenOpportunities: (item: CustomerWorkbenchItem) => void;
  onOpenCustomer: (item: CustomerWorkbenchItem) => void;
  onAssignPrimaryOwner: (item: CustomerWorkbenchItem) => void;
  onOpenSharedOwnerEditor: (item: CustomerWorkbenchItem) => void;
  onRemoveSharedOwner: (
    item: CustomerWorkbenchItem,
    userId: number,
    userName: string,
  ) => void;
  onDisableCustomer: (item: CustomerWorkbenchItem) => void;
  onEnableCustomer: (item: CustomerWorkbenchItem) => void;
  onDeleteCustomer: (item: CustomerWorkbenchItem) => void;
}): TableColumnProps<CustomerWorkbenchItem>[] {
  const visibleSet = new Set(visibleColumns);
  const columnByKey = new Map<
    CustomerWorkbenchColumnKey,
    TableColumnProps<CustomerWorkbenchItem>
  >();

  if (visibleSet.has("province")) {
    columnByKey.set("province", {
      title: (
        <SortableHeader
          label="省/市"
          field="province"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      width: 110,
      render: (_, item) => formatCustomerArea(item.province, item.city),
    });
  }
  if (visibleSet.has("orgUnit")) {
    columnByKey.set("orgUnit", {
      title: (
        <SortableHeader
          label="组织单元/校"
          field="org_unit_name"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      width: 220,
      render: (_, item) => (
        <div className="crm-customer-workbench-org-cell">
          <strong>{item.orgUnitPathName || item.orgUnitName || item.anchorLevel || "-"}</strong>
          <span>{item.orgName || "-"}</span>
        </div>
      ),
    });
  }
  if (visibleSet.has("customerName")) {
    columnByKey.set("customerName", {
      title: (
        <SortableHeader
          label="客户名称"
          field="customer_name"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "customerName",
      width: 180,
      render: (_, item) => (
        <button
          type="button"
          className="crm-customer-workbench-customer-name-wrap"
          disabled={!canAccessCrmOperation(permissionUser, "crm.customer:access", item.canEdit)}
          onClick={() => onOpenCustomer(item)}
        >
          {item.customerName}
          {item.isDisabled ? <Tag color="gray">停用</Tag> : null}
        </button>
      ),
    });
  }
  if (visibleSet.has("owners")) {
    columnByKey.set("owners", {
      title: "所属销售",
      width: 140,
      render: (_, item) => (
        <OwnerAssignmentCell
          item={item}
          permissionUser={permissionUser}
          onAssignPrimaryOwner={onAssignPrimaryOwner}
          onEditSharedOwners={onOpenSharedOwnerEditor}
          onRemoveSharedOwner={onRemoveSharedOwner}
        />
      ),
    });
  }
  if (visibleSet.has("contacts")) {
    columnByKey.set("contacts", {
      title: (
        <SortableHeader
          label="联系人"
          field="contact_count"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      width: 88,
      render: (_, item) => (
        item.contactCount > 0 && item.canOpenCustomer ? (
          <Button
            type="text"
            icon={<ContactRound size={15} />}
            onClick={() => onOpenContacts(item.id)}
          >
            {item.contactCount}
          </Button>
        ) : (
          <span aria-label={`${item.customerName}联系人数量`}>{item.contactCount}</span>
        )
      ),
    });
  }
  if (visibleSet.has("opportunities")) {
    columnByKey.set("opportunities", {
      title: (
        <SortableHeader
          label="商机"
          field="opportunity_count"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      width: 80,
      render: (_, item) =>
        item.opportunityCount > 0 ? (
          <Button
            type="text"
            className="crm-customer-workbench-link-button"
            aria-label={`查看${item.customerName}的商机`}
            onClick={() => onOpenOpportunities(item)}
          >
            {item.opportunityCount}
          </Button>
        ) : (
          <span aria-label={`${item.customerName}商机数量`}>{item.opportunityCount}</span>
        ),
    });
  }
  if (visibleSet.has("createdAt")) {
    columnByKey.set("createdAt", {
      title: (
        <SortableHeader
          label="创建时间"
          field="created_at"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "createdAt",
      width: 140,
    });
  }
  if (visibleSet.has("actions")) {
    columnByKey.set("actions", {
      title: "操作",
      width: 130,
      render: (_, item) => item.hasCustomerPermission ? (
        <Space size={6} wrap className="crm-customer-workbench-row-actions">
          <Tooltip content="编辑">
            <Button
              size="mini"
              shape="circle"
              aria-label={`编辑客户 ${item.customerName}`}
              icon={<Pencil size={14} />}
              disabled={!canAccessCrmOperation(permissionUser, "crm.customer:access", item.canEdit)}
              onClick={() => onOpenCustomer(item)}
            />
          </Tooltip>
          <Tooltip content={item.isDisabled ? "启用" : "停用"}>
            <Button
              size="mini"
              shape="circle"
              aria-label={`${item.isDisabled ? "启用" : "停用"}客户 ${item.customerName}`}
              icon={<Power size={14} />}
              disabled={!canAccessCrmOperation(permissionUser, "crm.customer:delete", item.canDelete)}
              onClick={() => (item.isDisabled ? onEnableCustomer(item) : onDisableCustomer(item))}
            />
          </Tooltip>
          <Tooltip content="删除">
            <Button
              size="mini"
              shape="circle"
              status="danger"
              aria-label={`删除客户 ${item.customerName}`}
              icon={<Trash2 size={14} />}
              disabled={!canAccessCrmOperation(permissionUser, "crm.customer:delete", item.canDelete)}
              onClick={() => onDeleteCustomer(item)}
            />
          </Tooltip>
        </Space>
      ) : (
        <Tag color="gray">只读</Tag>
      ),
    });
  }

  return visibleColumns
    .map((key) => columnByKey.get(key))
    .filter((column): column is TableColumnProps<CustomerWorkbenchItem> => Boolean(column));
}

/** 可直接指定升序或降序的表头。 */
function SortableHeader({
  label,
  field,
  activeField,
  activeDirection,
  onSort,
}: {
  label: string;
  field: string;
  activeField?: string;
  activeDirection: "asc" | "desc";
  onSort: (field: string, direction: "asc" | "desc") => void;
}) {
  const active = activeField === field;
  return (
    <div className="crm-customer-workbench-sort-header">
      <span>{label}</span>
      <span className="crm-customer-workbench-sort-actions">
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}升序`}
          type={active && activeDirection === "asc" ? "primary" : "secondary"}
          icon={<ArrowUp size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "asc");
          }}
        />
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}降序`}
          type={active && activeDirection === "desc" ? "primary" : "secondary"}
          icon={<ArrowDown size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "desc");
          }}
        />
      </span>
    </div>
  );
}

/** 紧凑展示客户主销售，并用弹层查看和维护共享销售。 */
function OwnerAssignmentCell({
  item,
  permissionUser,
  onAssignPrimaryOwner,
  onEditSharedOwners,
  onRemoveSharedOwner,
}: {
  item: CustomerWorkbenchItem;
  permissionUser: PermissionUser | null | undefined;
  onAssignPrimaryOwner: (item: CustomerWorkbenchItem) => void;
  onEditSharedOwners: (item: CustomerWorkbenchItem) => void;
  onRemoveSharedOwner: (
    item: CustomerWorkbenchItem,
    userId: number,
    userName: string,
  ) => void;
}) {
  const canManageOwners = canAccessCrmOperation(
    permissionUser,
    "crm.customer:assign",
    item.canAssign,
  );
  const sharedOwners = (item.sharedOwnerUserIds ?? []).map((userId, index) => ({
    userId,
    name: item.sharedOwnerUserNames?.[index] ?? `员工 ${userId}`,
  }));
  return (
    <div className="crm-customer-workbench-owner-list">
      <button
        type="button"
        disabled={!canManageOwners}
        onClick={() => onAssignPrimaryOwner(item)}
      >
        主：{formatCustomerOwners(item.primaryOwnerUserNames)}
      </button>
      {sharedOwners.length > 0 ? (
        <Popover
          position="left"
          trigger="hover"
          content={
            <div className="crm-customer-workbench-shared-owner-popover">
              <div className="crm-customer-workbench-shared-owner-popover-header">
                <strong>共享销售</strong>
                <Button
                  size="mini"
                  type="text"
                  disabled={!canManageOwners}
                  onClick={() => onEditSharedOwners(item)}
                >
                  修改
                </Button>
              </div>
              <div className="crm-customer-workbench-shared-owner-tags">
                {sharedOwners.map((owner) => (
                  <Tag key={`${owner.userId}-${owner.name}`} color="arcoblue">
                    <span>{owner.name}</span>
                    <Button
                      size="mini"
                      type="text"
                      className="crm-customer-workbench-shared-owner-remove"
                      icon={<Trash2 size={12} />}
                      aria-label={`移除共享销售 ${owner.name}`}
                      disabled={!canManageOwners}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemoveSharedOwner(item, owner.userId, owner.name);
                      }}
                    />
                  </Tag>
                ))}
              </div>
            </div>
          }
        >
          <button
            type="button"
            className="crm-customer-workbench-shared-owner-button"
            disabled={!canManageOwners}
            onClick={() => onEditSharedOwners(item)}
          >
            共享 {sharedOwners.length} 人
          </button>
        </Popover>
      ) : (
        <button
          type="button"
          className="crm-customer-workbench-shared-owner-button"
          disabled={!canManageOwners}
          onClick={() => onEditSharedOwners(item)}
        >
          共享 0 人
        </button>
      )}
    </div>
  );
}

/** 智能查询摘要。 */
function NlQuerySummary({ result }: { result: CustomerWorkbenchNlQueryParseResult | null }) {
  if (!result) {
    return null;
  }
  return (
    <Alert
      className="crm-list-alert crm-customer-workbench-alert"
      type={result.executable ? "success" : "warning"}
      showIcon
      content={[...result.summary, ...result.messages].join("；") || "未识别到可执行条件"}
    />
  );
}

/** 客户联系人抽屉内容。 */
function ContactDrawerContent({
  loading,
  page,
  onPageChange,
}: {
  loading: boolean;
  page?: ReturnType<typeof useCrmCustomerWorkbenchRuntime>["contactsPage"];
  onPageChange: (page: number) => void;
}) {
  return (
    <Spin loading={loading}>
      <div className="crm-customer-workbench-drawer-content">
        {(page?.items ?? []).map((item) => (
          <article className="crm-customer-workbench-contact-card" key={item.id}>
            <Space>
              {item.canOpenContact ? (
                <Button type="text" className="crm-customer-workbench-link-button">
                  {item.contactName}
                </Button>
              ) : (
                <Typography.Text>{item.contactName}</Typography.Text>
              )}
              {item.positionName ? <Tag>{item.positionName}</Tag> : null}
            </Space>
            <Typography.Text type="secondary">
              {item.orgUnitPathName || item.orgUnitName || "-"}
            </Typography.Text>
            <div className="crm-customer-workbench-contact-lines">
              <span>
                电话：
                {item.canViewSensitiveContactInfo ? item.mobile || "-" : "电话已隐藏"}
              </span>
              <span>
                微信：
                {item.canViewSensitiveContactInfo ? item.wechat || "-" : "微信已隐藏"}
              </span>
              <span>主销售：{formatCustomerOwners(item.primaryOwnerUserNames)}</span>
              <span>共享销售：{formatCustomerOwners(item.sharedOwnerUserNames)}</span>
              <span>最近动态：{item.latestRecordContent || "-"}</span>
            </div>
          </article>
        ))}
        <Pagination
          total={page?.total ?? 0}
          current={page?.page ?? 1}
          pageSize={page?.pageSize ?? 20}
          size="small"
          onChange={onPageChange}
        />
      </div>
    </Spin>
  );
}

/** 客户表单。 */
function CustomerForm({
  draft,
  detail,
  initialOrganization,
  initialUnit,
  onSearchOrganizations,
  onSearchOrganizationUnits,
  onChange,
}: {
  draft: CustomerWorkbenchFormPayload;
  detail?: CustomerWorkbenchDetail;
  initialOrganization?: CustomerWorkbenchOrganizationOption;
  initialUnit?: CustomerWorkbenchOrganizationUnitOption;
  onSearchOrganizations: (keyword: string) => Promise<CustomerWorkbenchOrganizationOption[]>;
  onSearchOrganizationUnits: (
    orgId: number,
    keyword: string,
  ) => Promise<CustomerWorkbenchOrganizationUnitOption[]>;
  onChange: (draft: CustomerWorkbenchFormPayload) => void;
}) {
  const [organizationOptions, setOrganizationOptions] = useState<CustomerWorkbenchOrganizationOption[]>(
    [],
  );
  const [unitOptions, setUnitOptions] = useState<CustomerWorkbenchOrganizationUnitOption[]>([]);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [unitLoading, setUnitLoading] = useState(false);
  const unitSearchSequenceRef = useRef(0);

  useEffect(() => {
    const currentOrg =
      draft.orgId && (initialOrganization || detail?.orgName || draft.orgId)
        ? [
            initialOrganization?.id === draft.orgId
              ? initialOrganization
              : { id: draft.orgId, orgName: detail?.orgName ?? `学校 ${draft.orgId}` },
          ]
        : [];
    const currentUnit =
      draft.anchorOrgUnitId && draft.orgId
        ? [
            initialUnit?.id === draft.anchorOrgUnitId
              ? initialUnit
              : {
                  id: draft.anchorOrgUnitId,
                  orgId: draft.orgId,
                  unitName: detail?.anchorOrgUnitName ?? detail?.anchorOrgUnitPathName ?? "",
                  unitPathName: detail?.anchorOrgUnitPathName ?? detail?.anchorOrgUnitName ?? "",
                  levelNo: mapAnchorLevelToLevelNo(draft.anchorLevel),
                  unitType: draft.anchorUnitType ?? detail?.anchorOrgUnitType,
                },
          ]
        : [];
    setOrganizationOptions((current) => mergePreferLastById([...currentOrg, ...current]));
    setUnitOptions((current) => dedupeById([...currentUnit, ...current]));
  }, [
    detail?.anchorOrgUnitName,
    detail?.anchorOrgUnitPathName,
    detail?.orgName,
    draft.anchorOrgUnitId,
    draft.anchorLevel,
    draft.orgId,
    initialOrganization,
    initialUnit,
  ]);

  /** 远程搜索学校候选项，并保留当前已选学校。 */
  async function handleSearchOrganizations(keyword: string) {
    setOrganizationLoading(true);
    try {
      const options = await onSearchOrganizations(keyword);
      setOrganizationOptions(mergeOrganizationOptions(options, draft, detail));
    } finally {
      setOrganizationLoading(false);
    }
  }

  /** 远程搜索当前学校下的院系专业候选项。 */
  async function handleSearchUnits(keyword: string) {
    if (!draft.orgId) {
      setUnitOptions([]);
      return;
    }
    const searchSequence = unitSearchSequenceRef.current + 1;
    unitSearchSequenceRef.current = searchSequence;
    setUnitLoading(true);
    try {
      const options = await onSearchOrganizationUnits(draft.orgId, keyword);
      if (unitSearchSequenceRef.current === searchSequence) {
        setUnitOptions(mergeUnitOptions(options, draft, detail));
      }
    } finally {
      if (unitSearchSequenceRef.current === searchSequence) {
        setUnitLoading(false);
      }
    }
  }

  /** 选择学校后清空旧组织单元，并按学校名生成客户名。 */
  function handleOrganizationSelect(value: string | number | undefined) {
    const selected = organizationOptions.find((option) => option.id === Number(value));
    if (!selected) {
      onChange({
        ...draft,
        orgId: 0,
        anchorOrgUnitId: undefined,
        anchorLevel: "school",
        customerName: "",
        customerStar: undefined,
        anchorUnitType: undefined,
      });
      setUnitOptions([]);
      return;
    }
    unitSearchSequenceRef.current += 1;
    const nextDraft = {
      ...draft,
      orgId: selected.id,
      anchorOrgUnitId: undefined,
      anchorLevel: "school",
      customerName: selected.orgName,
      customerStar: undefined,
      anchorUnitType: undefined,
    };
    onChange(nextDraft);
    setOrganizationOptions((current) => mergePreferLastById([...current, selected]));
    void onSearchOrganizationUnits(selected.id, "").then((options) => {
      setUnitOptions(filterUnitOptionsByOrgId(options, selected.id));
    });
  }

  /** 选择组织单元后用学校 + 组织单元全路径生成客户名。 */
  function handleUnitSelect(value: string | number | undefined) {
    const selected = unitOptions.find((option) => option.id === Number(value));
    const orgName =
      organizationOptions.find((option) => option.id === draft.orgId)?.orgName ??
      detail?.orgName ??
      "";
    const unitType = selected?.unitType;
    onChange({
      ...draft,
      anchorOrgUnitId: selected?.id,
      anchorLevel: selected ? mapAnchorLevel(selected.levelNo) : "school",
      anchorUnitType: unitType,
      customerStar: shouldShowCustomerStar(unitType) ? draft.customerStar : undefined,
      customerName: selected
        ? [orgName, ...(selected.unitPathName || selected.unitName).split("/")]
            .map((part) => part.trim())
            .filter(Boolean)
            .join("_")
        : orgName,
    });
  }

  const inferredAnchorLevel = getAnchorLevelLabel(draft.anchorLevel);
  const showCustomerStar = shouldShowCustomerStar(
    draft.anchorUnitType ??
      (initialUnit?.id === draft.anchorOrgUnitId ? initialUnit?.unitType : undefined) ??
      (detail?.anchorOrgUnitId === draft.anchorOrgUnitId ? detail?.anchorOrgUnitType : undefined),
  );

  return (
    <div className="crm-customer-workbench-editor-form admin-editor-form">
      <div className="crm-customer-workbench-editor-section">
        <Typography.Text className="crm-customer-workbench-editor-section-title">
          客户归属
        </Typography.Text>
      </div>
      <Form.Item label="学校" required>
        <Select
          data-testid="customer-school-select"
          allowClear
          showSearch
          loading={organizationLoading}
          value={draft.orgId || undefined}
          placeholder="搜索学校名称"
          filterOption={false}
          onSearch={(keyword) => void handleSearchOrganizations(keyword)}
          onFocus={() => void handleSearchOrganizations(detail?.orgName ?? "")}
          onChange={handleOrganizationSelect}
        >
          {organizationOptions.map((option) => (
            <Select.Option key={option.id} value={option.id}>
              {formatOrganizationOption(option)}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label="组织单元">
        <Select
          data-testid="customer-unit-select"
          allowClear
          showSearch
          disabled={!draft.orgId}
          loading={unitLoading}
          value={draft.anchorOrgUnitId}
          placeholder={draft.orgId ? "搜索二级学院/系/专业" : "请先选择学校"}
          filterOption={false}
          onSearch={(keyword) => void handleSearchUnits(keyword)}
          onFocus={() => void handleSearchUnits("")}
          onChange={handleUnitSelect}
        >
          {filterUnitOptionsByOrgId(unitOptions, draft.orgId).map((option) => (
            <Select.Option key={option.id} value={option.id}>
              {option.unitPathName || option.unitName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label="客户名" required>
        <Input
          placeholder="选择学校和组织单元后自动生成"
          value={draft.customerName}
          readOnly
        />
      </Form.Item>
      <Form.Item label="覆盖方式">
        <Select
          value={draft.coverageMode}
          onChange={(coverageMode) => onChange({ ...draft, coverageMode: String(coverageMode) })}
        >
          <Select.Option value="anchor">锚点覆盖</Select.Option>
          <Select.Option value="descendant">含下级</Select.Option>
        </Select>
      </Form.Item>
      <div className="crm-customer-workbench-derived-line">
        <span>自动层级</span>
        <strong>{inferredAnchorLevel}</strong>
      </div>
      {showCustomerStar ? (
        <Form.Item label="星级标签" required>
          <Radio.Group
            className="crm-customer-workbench-star-group"
            value={draft.customerStar}
            onChange={(customerStar) =>
              onChange({ ...draft, customerStar: String(customerStar) })
            }
          >
            <div className="crm-customer-workbench-star-list">
              {CUSTOMER_STAR_OPTIONS.map((star) => (
                <Radio key={star} value={star}>
                  <span className="crm-customer-workbench-star-option">
                    <span className="crm-customer-workbench-star-option-code">{star}</span>
                    <span className="crm-customer-workbench-star-option-desc">
                      （{CUSTOMER_STAR_OPTION_DESCRIPTIONS[star]}）
                    </span>
                  </span>
                </Radio>
              ))}
            </div>
          </Radio.Group>
        </Form.Item>
      ) : (
        <Form.Item label="客户状态">
          <Radio.Group
            value={draft.businessStatus}
            onChange={(businessStatus) =>
              onChange({ ...draft, businessStatus: String(businessStatus) })
            }
          >
            <div className="crm-customer-workbench-status-grid">
              {customerBusinessStatusOptions.map((status) => (
                <Radio key={status} value={status}>
                  {status}
                </Radio>
              ))}
            </div>
          </Radio.Group>
        </Form.Item>
      )}
      <Form.Item label="备注">
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 5 }}
          value={draft.remark}
          onChange={(remark) => onChange({ ...draft, remark })}
        />
      </Form.Item>
    </div>
  );
}

/** 员工下拉选择器。 */
function StaffSelect({
  "data-testid": testId,
  value,
  options,
  placeholder,
  allowClear = false,
  onChange,
}: {
  "data-testid"?: string;
  value?: number;
  options: CustomerWorkbenchStaffOption[];
  placeholder: string;
  allowClear?: boolean;
  onChange: (value: number | undefined, displayName?: string) => void;
}) {
  return (
    <Select
      data-testid={testId}
      allowClear={allowClear}
      showSearch
      placeholder={placeholder}
      value={value}
      filterOption={(inputValue, option) => {
        const optionProps = option.props as { value?: number | string; children?: unknown };
        const matchedOption = options.find(
          (item) => String(item.id) === String(optionProps.value),
        );
        const searchText = matchedOption
          ? buildStaffOptionSearchText(matchedOption)
          : String(optionProps.children ?? "");
        return searchText.toLowerCase().includes(inputValue.toLowerCase());
      }}
      onChange={(nextValue) => {
        if (!nextValue) {
          onChange(undefined, undefined);
          return;
        }
        const selectedId = Number(nextValue);
        const selected = options.find((item) => item.id === selectedId);
        onChange(
          selectedId,
          selected ? formatCustomerStaffOptionLabel(selected) : undefined,
        );
      }}
    >
      {options.map((option) => {
        const label = formatCustomerStaffOptionLabel(option);
        return (
          <Select.Option
            key={option.id}
            value={option.id}
            disabled={option.disabled}
          >
            {label}
          </Select.Option>
        );
      })}
    </Select>
  );
}

/** 把 URL 回填的销售名并入下拉候选，避免只显示数字 ID。 */
function mergeSalesFilterStaffOptions(
  options: CustomerWorkbenchStaffOption[],
  salesUserId?: number,
  salesUserName?: string,
): CustomerWorkbenchStaffOption[] {
  if (!salesUserId) {
    return options;
  }
  if (options.some((option) => option.id === salesUserId)) {
    return options;
  }
  return [
    {
      id: salesUserId,
      username: "",
      displayName: salesUserName?.trim() || "已选销售",
      disabled: false,
    },
    ...options,
  ];
}

/** 生成员工选项搜索文本。 */
function buildStaffOptionSearchText(option: CustomerWorkbenchStaffOption) {
  const label = formatCustomerStaffOptionLabel(option);
  return `${label} ${option.username} ${option.phone ?? ""} ${option.id}`;
}

/** 千分位展示客户数量。 */
function formatCustomerCount(value: number) {
  return new Intl.NumberFormat("zh-CN").format(value);
}

/** 判断当前选择是否完整覆盖当前页（用于「当前页」范围按钮高亮）。 */
function isCurrentPageSelectionActive(
  selectionMode: CustomerWorkbenchSelectionMode,
  selectedRowKeys: number[],
  currentPageCustomerIds: number[],
) {
  if (selectionMode !== "selected_rows" || currentPageCustomerIds.length === 0) {
    return false;
  }
  if (selectedRowKeys.length !== currentPageCustomerIds.length) {
    return false;
  }
  const selectedIds = new Set(selectedRowKeys);
  return currentPageCustomerIds.every((id) => selectedIds.has(id));
}

/** 合并学校搜索结果和当前已选学校。 */
function mergeOrganizationOptions(
  options: CustomerWorkbenchOrganizationOption[],
  draft: CustomerWorkbenchFormPayload,
  detail?: CustomerWorkbenchDetail,
) {
  const current =
    draft.orgId && (detail?.orgName || draft.orgId)
      ? [{ id: draft.orgId, orgName: detail?.orgName ?? `学校 ${draft.orgId}` }]
      : [];
  return mergePreferLastById([...current, ...options]);
}

/** 合并院系搜索结果和当前已选院系。 */
function mergeUnitOptions(
  options: CustomerWorkbenchOrganizationUnitOption[],
  draft: CustomerWorkbenchFormPayload,
  detail?: CustomerWorkbenchDetail,
) {
  const orgScopedOptions = filterUnitOptionsByOrgId(options, draft.orgId);
  const current =
    draft.anchorOrgUnitId && draft.orgId
      ? [
          {
            id: draft.anchorOrgUnitId,
            orgId: draft.orgId,
            unitName: detail?.anchorOrgUnitName ?? detail?.anchorOrgUnitPathName ?? "",
            unitPathName: detail?.anchorOrgUnitPathName ?? detail?.anchorOrgUnitName ?? "",
            levelNo: mapAnchorLevelToLevelNo(draft.anchorLevel),
          },
        ]
      : [];
  return dedupeById([...current, ...orgScopedOptions]);
}

/** 格式化学校候选项显示。 */
function formatOrganizationOption(option: CustomerWorkbenchOrganizationOption) {
  const area = [option.province, option.city].filter(Boolean).join(" / ");
  return area ? `${option.orgName} (${area})` : option.orgName;
}

/** 只保留当前学校下的院系专业候选项。 */
function filterUnitOptionsByOrgId(
  options: CustomerWorkbenchOrganizationUnitOption[],
  orgId?: number,
) {
  if (!orgId) {
    return [];
  }
  return options.filter((option) => option.orgId === orgId);
}

/** 根据组织单元层级推断客户锚点层级。 */
function mapAnchorLevel(levelNo?: number) {
  if (levelNo === 1) {
    return "college";
  }
  if (levelNo === 2) {
    return "department";
  }
  if ((levelNo ?? 0) >= 3) {
    return "major";
  }
  return "school";
}

/** 根据客户锚点层级回填组织单元候选层级号。 */
function mapAnchorLevelToLevelNo(anchorLevel?: string) {
  const levelMap: Record<string, number> = {
    school: 0,
    college: 1,
    department: 2,
    major: 3,
  };
  return levelMap[anchorLevel ?? "school"] ?? 0;
}

/** 显示自动推断的客户组织层级。 */
function getAnchorLevelLabel(anchorLevel: string) {
  const levelMap: Record<string, string> = {
    school: "学校",
    college: "学院",
    department: "系",
    major: "专业",
  };
  return levelMap[anchorLevel] ?? anchorLevel;
}

/** 去除重复 ID。 */
function dedupeById<T extends { id: number }>(values: T[]) {
  const ids = new Set<number>();
  return values.filter((item) => {
    if (ids.has(item.id)) {
      return false;
    }
    ids.add(item.id);
    return true;
  });
}

/** 按 ID 合并选项，后出现的真实远程候选覆盖早先兜底项。 */
function mergePreferLastById<T extends { id: number }>(values: T[]) {
  const itemMap = new Map<number, T>();
  values.forEach((item) => {
    itemMap.set(item.id, item);
  });
  return Array.from(itemMap.values());
}
